import styled from "@emotion/styled"
import { Image } from "theme-ui"
import { useLocation } from "@reach/router"
import React, { useContext } from "react"
import { InterfaceContext } from "../../context/interface-context"
import { useNotificationContext } from "../../context/notification-context"
import StoreContext from "../../context/store-context"
import HeaderLogoDark from "../../assets/images/header-logo.png"
import { navigate, StaticQuery } from "gatsby"

import MenuIcon from "../../assets/icons/menu.svg"
import SearchIcon from "../../assets/icons/search-new.svg"
import CartIcon from "../../assets/icons/cart.svg"
import CrossIcon from "../../assets/icons/cross.svg"
import { graphql } from 'gatsby'

// HEADER
const SiteHeaderDesktop = styled.div`
  display: none;

  ${({ theme }) => theme.bp.largeDesktop} {
    display: flex;
  }

  position: sticky;
  top: 0;
  z-index: 100;
`

const SiteHeaderMobile = styled.div`
  display: block;

  ${({ theme }) => theme.bp.largeDesktop} {
    display: none;
  }

  position: sticky;
  top: 0;
  z-index: 100;
`

const SiteHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: ${({ theme }) => theme.height[0]}px;
  width: 100%;
  padding-inline: 15px;
  color: ${({ theme }) => theme.colors.darkerGreen};
  border-bottom: 1px solid ${({ theme }) => theme.colors.darkerGreen};
  background-color: ${({ theme }) => theme.colors.white};

  ${({ theme }) => theme.bp.largeDesktop} {
    height: ${({ theme }) => theme.height[1]}px;
    padding-inline: 30px;
  }
`

// NAVIGATION
const SiteHeaderNavDesktop = styled.nav`
  color: inherit;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: ${({ theme }) => theme.fontSizes[1]}px;

  ${({ theme }) => theme.bp.largerDesktop} {
    gap: 22px;
    font-size: ${({ theme }) => theme.fontSizes[2]}px;
  }
`

const SiteHeaderNavMobile = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  overflow-x: auto;
  border-bottom: 1px solid ${({ theme }) => theme.colors.darkerGreen};
  background-color: ${({ theme }) => theme.colors.white};
  height: 36px;
  padding-inline: 15px;
  font-size: ${({ theme }) => theme.fontSizes[1]}px;

  &::before {
    content: "";
    position: absolute;
    bottom: 1px;
    right: 0;
    height: 35px;
    width: 25px;
    background: linear-gradient(to left, white, transparent);
    pointer-events: none;
    z-index: 1;
  }
`

const SiteHeaderNavItem = styled.span`
  flex-shrink: 0;
  transition: color 0.2s ease-in-out;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.lightGreen};
  }
`

// EXTRAS (right side of header)
const SiteHeaderExtras = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`

const SiteHeaderExtrasItem = styled.div`
  flex-shrink: 0;
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  cursor: pointer;
  transition: color 0.2s ease-in-out;

  &:hover {
    color: ${({ theme }) => theme.colors.lightGreen};
  }

  ${({ theme }) => theme.bp.largeDesktop} {
  }
`

// MOBILE NAVIGATION TOGGLE
const SiteHeaderMobileNavOpen = styled(MenuIcon)`
  width: 24px;
  height: 24px;
  cursor: pointer;
  transition: color 0.2s ease-in-out;
  flex-shrink: 0;

  &:hover {
    color: ${({ theme }) => theme.colors.lightGreen};
  }
`

// MOBILE NAVIGATION TOGGLE
const SiteHeaderMobileNavClose = styled(CrossIcon)`
  width: 18px;
  height: 18px;
  cursor: pointer;
  transition: color 0.2s ease-in-out;
  fill: ${({ theme }) => theme.colors.darkerGreen};
  margin-inline: 3px;

  &:hover {
    color: ${({ theme }) => theme.colors.lightGreen};
  }
`

const SiteHeaderSearchDrawerToggle = styled(SearchIcon)`
  margin-top: 4px;
`

const Header = () => {
  const {
    toggleSearch,
    toggleProductNav,
    toggleRegionOpen,
    showCart,
    hideDrawers,
    isProductNavOpen,
  } = useContext(InterfaceContext)

  const { cart } = useContext(StoreContext)
  const { pathname } = useLocation()
  const { setNotificationList } = useNotificationContext()

  const hideMenuLocations = ["checkout", "payment"]
  const hideMenuItems = hideMenuLocations.some((location) => {
    return pathname.includes(location)
  })

  const handleClick = (node) => {
    if (node.slug) {
      hideDrawers()
      navigate(`/${node.slug}`)
    }
  }

  return (
    <StaticQuery
      query={graphql`
        {
          productNav: contentfulNavigationMenu(name: { eq: "Products" }) {
            items {
              ... on ContentfulCategory {
                name
                slug
                id
              }
            }
            name
            id
          }
        }
      `}
      render={({ productNav }) => (
        <>
          <SiteHeaderDesktop>
            <SiteHeader>
              <Image
                onClick={() => navigate("/")}
                src={HeaderLogoDark}
                sx={{
                  width: "125px",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              />
              <SiteHeaderNavDesktop>
                {productNav.items.map((item, index) => (
                  <SiteHeaderNavItem
                    key={index}
                    onMouseOver={hideDrawers}
                    onClick={() => handleClick(item)}
                  >
                    {item.name}
                  </SiteHeaderNavItem>
                ))}
              </SiteHeaderNavDesktop>
              {!hideMenuItems && (
                <SiteHeaderExtras>
                  <SiteHeaderExtrasItem onClick={toggleRegionOpen}>
                    {cart.countryCode?.toUpperCase()}—
                    {cart.currencyCode?.toUpperCase()}
                  </SiteHeaderExtrasItem>
                  <SiteHeaderExtrasItem onClick={toggleSearch}>
                    <SiteHeaderSearchDrawerToggle />
                  </SiteHeaderExtrasItem>
                  <SiteHeaderExtrasItem onClick={() => showCart()}>
                    <CartIcon />
                  </SiteHeaderExtrasItem>
                </SiteHeaderExtras>
              )}
            </SiteHeader>
          </SiteHeaderDesktop>

          <SiteHeaderMobile>
            <SiteHeader>
              {isProductNavOpen ? (
                <SiteHeaderMobileNavClose
                  onClick={() => {
                    toggleProductNav()
                    setNotificationList((list) => {
                      const result = [...list]
                      result.splice(0, 1)
                      return result
                    })
                  }}
                />
              ) : (
                <SiteHeaderMobileNavOpen
                  onClick={() => {
                    toggleProductNav()
                    setNotificationList((list) => {
                      const result = [...list]
                      result.splice(0, 1)
                      return result
                    })
                  }}
                />
              )}

              <Image
                onClick={() => navigate("/")}
                src={HeaderLogoDark}
                sx={{
                  width: "104px",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              />
              {!hideMenuItems && (
                <SiteHeaderExtras>
                  <SiteHeaderExtrasItem onClick={toggleSearch}>
                    <SiteHeaderSearchDrawerToggle />
                  </SiteHeaderExtrasItem>
                  <SiteHeaderExtrasItem onClick={() => showCart()}>
                    <CartIcon />
                  </SiteHeaderExtrasItem>
                </SiteHeaderExtras>
              )}
            </SiteHeader>
            <SiteHeaderNavMobile>
              {productNav.items.map((item, index) => (
                <SiteHeaderNavItem
                  key={index}
                  onMouseOver={hideDrawers}
                  onClick={() => handleClick(item)}
                >
                  {item.name}
                </SiteHeaderNavItem>
              ))}
            </SiteHeaderNavMobile>
          </SiteHeaderMobile>
        </>
      )}
    />
  )
}

export default Header
